import { api } from './apiClient';

export interface StockItem {
  id: number;
  productId: number;
  productName: string;
  productCode: string;
  categoryName: string;
  warehouseId: number;
  warehouseName: string;
  currentStock: number;
  minimumStock: number;
  maximumStock: number;
  reorderLevel: number;
  averageCost: number;
  stockValue: number;
  unit: string;
  isActive: boolean;
  lastUpdated: string;
}

export interface StockMovement {
  id: number;
  productId: number;
  productName: string;
  productCode: string;
  warehouseId: number;
  warehouseName: string;
  movementType: 'IN' | 'OUT' | 'ADJUSTMENT';
  quantity: number;
  referenceType: string;
  referenceId: number | null;
  unitCost: number;
  movementDate: string;
  remarks: string;
  createdBy: string;
  createdAt: string;
}

export interface Warehouse {
  id: number;
  warehouseCode: string;
  warehouseName: string;
  address: string;
  city: string;
  state: string;
  pincode: string;
  isActive: boolean;
  createdAt: string;
}

export interface StockSummary {
  totalProducts: number;
  totalStockValue: number;
  lowStockCount: number;
  outOfStockCount: number;
  recentMovementsCount: number;
}

export interface StockAdjustment {
  productId: number;
  warehouseId?: number;
  movementType: 'IN' | 'OUT' | 'ADJUSTMENT';
  quantity: number;
  reason: string;
  referenceType?: string;
  referenceId?: number;
  unitCost?: number;
}

export const inventoryService = {
  // Stock
  getStock: (params?: any) =>
    api.get<{ stock: StockItem[]; count: number }>('/inventory/stock', { params }),

  getStockById: (id: number) =>
    api.get<{ stock: StockItem }>(`/inventory/stock/${id}`),

  getStockByProduct: (productId: number) =>
    api.get<{ stock: StockItem[]; count: number }>(`/inventory/stock/product/${productId}`),

  updateStockLevels: (id: number, data: { minimumStock?: number; maximumStock?: number; reorderLevel?: number }) =>
    api.put(`/inventory/stock/${id}/levels`, data),

  adjustStock: (data: StockAdjustment) =>
    api.post('/inventory/stock/adjust', data),

  // Movements
  getMovements: (params?: any) =>
    api.get<{ movements: StockMovement[]; count: number }>('/inventory/movements', { params }),

  recordMovement: (data: StockAdjustment) =>
    api.post('/inventory/movements', data),

  // Alerts & Summary
  getAlerts: () =>
    api.get<{ alerts: StockItem[]; count: number }>('/inventory/alerts'),

  getSummary: () =>
    api.get<{ summary: StockSummary }>('/inventory/summary'),

  // Warehouses
  getWarehouses: (params?: any) =>
    api.get<{ warehouses: Warehouse[]; count: number }>('/inventory/warehouses', { params }),

  createWarehouse: (data: Omit<Warehouse, 'id' | 'createdAt'>) =>
    api.post<{ warehouse: Warehouse }>('/inventory/warehouses', data),

  updateWarehouse: (id: number, data: Partial<Warehouse>) =>
    api.put<{ warehouse: Warehouse }>(`/inventory/warehouses/${id}`, data),
};
